// The playground page: a full-height toolbar on the left, then the top bar,
// the editor with a tab per open script, output and (for libraries that draw)
// a viewport. Without a viewport the output takes the right-hand pane. The
// toolbar opens the script and model libraries, which float over the
// workspace, and shows or hides the editor and output.

import { el, icon, ICONS } from './dom.js';

/**
 * @param {object} options
 * @param {string} options.title     Library name shown next to the logo.
 * @param {string} options.home      Link behind the logo (the chooser).
 * @param {string} options.logo
 * @param {boolean} options.viewport Mount a viewer pane on the right.
 * @param {string} [options.accept]  File types the model library takes. None: no model library.
 * @param {string} [options.hint]    Bottom-right hint over the viewport.
 */
export function mount( { title, home, logo, viewport: withViewport, accept, hint } ) {

	const modelLabel = el( 'span', { class: 'pg-model-label', id: 'pg-model-label' } );
	const modelChip = accept ? el( 'button', {
		class: 'pg-model-chip', id: 'pg-model-chip', type: 'button', title: 'Model library', 'aria-controls': 'pg-models',
	}, [ icon( ICONS.model ), modelLabel ] ) : null;
	const file = accept ? el( 'input', { id: 'pg-file', type: 'file', accept, hidden: true } ) : null;
	const run = el( 'button', { id: 'pg-run', class: 'pg-btn pg-btn--primary pg-btn--sm', type: 'button', title: 'Run (Ctrl+Enter)' }, [
		icon( ICONS.run ),
		el( 'span', { text: 'Run' } ),
	] );
	const stop = el( 'button', { id: 'pg-stop', class: 'pg-btn pg-btn--sm', type: 'button', title: 'Stop (Esc)', disabled: true }, [
		icon( ICONS.stop ),
		el( 'span', { text: 'Stop' } ),
	] );
	const revert = el( 'button', { id: 'pg-revert', class: 'pg-btn pg-btn--ghost pg-btn--sm', type: 'button', title: 'Discard your edits to this script', text: 'Revert' } );
	const reset = withViewport
		? el( 'button', { id: 'pg-reset', class: 'pg-btn pg-btn--ghost', type: 'button', title: 'Show all, clear colours and selection', text: 'Reset view' } )
		: null;
	const tabs = el( 'div', { class: 'pg-tabs', id: 'pg-tabs', role: 'tablist', 'aria-label': 'Open scripts' } );
	const tabbar = el( 'div', { class: 'pg-tabbar' }, [ tabs, revert ] );
	const scriptsButton = el( 'button', {
		class: 'pg-icon pg-tool', id: 'pg-scripts-btn', type: 'button',
		title: 'Script library', 'aria-label': 'Script library', 'aria-expanded': 'false', 'aria-controls': 'pg-scripts',
	}, [ icon( ICONS.scripts ) ] );
	const modelsButton = accept ? el( 'button', {
		class: 'pg-icon pg-tool', id: 'pg-models-btn', type: 'button',
		title: 'Model library', 'aria-label': 'Model library', 'aria-expanded': 'false', 'aria-controls': 'pg-models',
	}, [ icon( ICONS.model ) ] ) : null;
	const editorButton = el( 'button', {
		class: 'pg-icon pg-tool is-active', id: 'pg-editor-btn', type: 'button',
		title: 'Code editor', 'aria-label': 'Code editor', 'aria-pressed': 'true',
	}, [ icon( ICONS.editor ) ] );
	const outputButton = el( 'button', {
		class: 'pg-icon pg-tool is-active', id: 'pg-output-btn', type: 'button',
		title: 'Output', 'aria-label': 'Output', 'aria-pressed': 'true',
	}, [ icon( ICONS.terminal ) ] );
	const toolbar = el( 'nav', { class: 'pg-toolbar', 'aria-label': 'Tools' }, [
		el( 'a', { class: 'pg-toolbar-logo', href: home, title: 'Choose another playground' }, [
			el( 'img', { src: logo, alt: '', width: '22', height: '22' } ),
		] ),
		scriptsButton,
		modelsButton,
		el( 'span', { class: 'pg-toolbar-sep', 'aria-hidden': 'true' } ),
		editorButton,
		outputButton,
	] );
	const scriptsFilter = el( 'input', { class: 'pg-scripts-filter', id: 'pg-scripts-filter', type: 'search', placeholder: 'Filter scripts', 'aria-label': 'Filter scripts' } );
	const scriptsClose = el( 'button', { class: 'pg-icon', type: 'button', title: 'Close (Esc)', 'aria-label': 'Close scripts' }, [ icon( ICONS.close ) ] );
	const scriptsTree = el( 'div', { class: 'pg-tree', id: 'pg-tree', role: 'tree', 'aria-label': 'Example scripts' } );
	const scriptsPanel = el( 'aside', { class: 'pg-panel', id: 'pg-scripts', 'aria-label': 'Scripts', hidden: true }, [
		el( 'div', { class: 'pg-panel-head' }, [ el( 'span', { text: 'Scripts' } ), scriptsClose ] ),
		scriptsFilter,
		scriptsTree,
	] );
	const modelsClose = el( 'button', { class: 'pg-icon', type: 'button', title: 'Close (Esc)', 'aria-label': 'Close models' }, [ icon( ICONS.close ) ] );
	const modelsList = el( 'div', { class: 'pg-tree', id: 'pg-models-list', role: 'listbox', 'aria-label': 'Models' } );
	const modelsPanel = accept ? el( 'aside', { class: 'pg-panel pg-panel--models', id: 'pg-models', 'aria-label': 'Models', hidden: true }, [
		el( 'div', { class: 'pg-panel-head' }, [ el( 'span', { text: 'Models' } ), modelsClose ] ),
		modelsList,
	] ) : null;
	const editor = el( 'div', { class: 'pg-editor', id: 'pg-editor' }, [
		el( 'p', { class: 'pg-editor-loading', text: 'Loading editor…' } ),
	] );
	const consoleMeta = el( 'span', { class: 'pg-console-meta', id: 'pg-console-meta' } );
	const consoleClear = el( 'button', { class: 'pg-icon', id: 'pg-console-clear', type: 'button', 'aria-label': 'Clear output', title: 'Clear output' }, [
		icon( ICONS.clear ),
	] );
	const output = el( 'div', { class: 'pg-console-body', id: 'pg-console', role: 'log', 'aria-live': 'polite' } );
	const consolePanel = el( 'div', { class: 'pg-console' }, [
		el( 'div', { class: 'pg-console-head' }, [
			el( 'span', { text: 'Output' } ),
			consoleMeta,
			run,
			stop,
			consoleClear,
		] ),
		output,
	] );
	const statusText = el( 'span', { id: 'pg-status-text', text: `Starting ${ title }…` } );
	const status = el( 'div', { class: 'pg-status', id: 'pg-status', role: 'status' }, [
		el( 'span', { class: 'pg-spinner', 'aria-hidden': 'true' } ),
		statusText,
	] );

	const hsplit = withViewport
		? el( 'div', { class: 'pg-hsplit', id: 'pg-hsplit', role: 'separator', 'aria-orientation': 'horizontal', 'aria-label': 'Resize output' } )
		: null;
	const left = el( 'section', { class: 'pg-left', 'aria-label': 'Script editor' }, withViewport
		? [ tabbar, editor, hsplit, consolePanel ]
		: [ tabbar, editor ] );
	const vsplit = el( 'div', { class: 'pg-vsplit', id: 'pg-vsplit', role: 'separator', 'aria-orientation': 'vertical', 'aria-label': 'Resize editor' } );

	let viewport = null;
	let pick = null;
	let right;
	if ( withViewport ) {

		viewport = el( 'div', { class: 'pg-viewport', id: 'pg-viewport' } );
		pick = el( 'div', { class: 'pg-pick', id: 'pg-pick', hidden: true } );
		right = el( 'section', { class: 'pg-right', 'aria-label': `${ title } viewer` }, [
			viewport,
			status,
			pick,
			hint ? el( 'p', { class: 'pg-hint', text: hint } ) : null,
		] );

	} else {

		right = el( 'section', { class: 'pg-right pg-right--output', 'aria-label': 'Output' }, [ consolePanel, status ] );

	}

	const main = el( 'main', { class: 'pg-main', id: 'pg-main' }, [ left, vsplit, right, scriptsPanel, modelsPanel ] );
	const root = el( 'div', { class: withViewport ? 'pg' : 'pg pg--no-viewport', id: 'pg' }, [ toolbar, el( 'div', { class: 'pg-shell' }, [
		el( 'header', { class: 'pg-bar' }, [
			el( 'a', { class: 'pg-brand', href: home, title: 'Choose another playground' }, [
				el( 'span', { text: 'Playground' } ),
				el( 'span', { class: 'pg-brand-lib', text: title } ),
			] ),
			el( 'span', { class: 'pg-spacer' } ),
			modelChip,
			file,
			reset,
		] ),
		main,
	] ) ] );

	document.body.append( root );
	return {
		root, main, left, file, run, stop, revert, reset, tabs,
		scriptsButton, editorButton, outputButton, scriptsPanel, scriptsFilter, scriptsClose, scriptsTree,
		modelsButton, modelsPanel, modelsClose, modelsList, modelChip, modelLabel,
		editor, console: output, consoleMeta, consoleClear,
		viewport, right, status, statusText, pick, vsplit, hsplit,
	};

}

// ---- Status pill ------------------------------------------------------------

export function createStatus( els ) {

	let timer = 0;
	return function status( text, { error = false, settle = 0 } = {} ) {

		clearTimeout( timer );
		els.statusText.textContent = text;
		els.status.classList.toggle( 'is-error', error );
		els.status.classList.remove( 'is-idle' );
		if ( settle ) timer = setTimeout( () => els.status.classList.add( 'is-idle' ), settle );

	};

}

// ---- Pick card --------------------------------------------------------------

/** The card over the viewport naming the picked object. Null hides it. */
export function createPickCard( els ) {

	return function showPick( row ) {

		if ( ! els.pick ) return;
		if ( ! row ) {

			els.pick.hidden = true;
			return;

		}

		els.pick.textContent = '';
		const title = document.createElement( 'strong' );
		title.textContent = row.name || '(unnamed)';
		const type = document.createElement( 'div' );
		type.textContent = row.type ?? '';
		const guid = document.createElement( 'code' );
		guid.textContent = row.guid ?? '';
		els.pick.append( title, type, guid );
		els.pick.hidden = false;

	};

}

// ---- Pane toggles -----------------------------------------------------------

/** The toolbar's editor and output buttons show or hide those panes. */
export function bindPaneToggles( els ) {

	const toggle = ( button, hiddenClass ) => button.addEventListener( 'click', () => {

		const shown = ! els.root.classList.toggle( hiddenClass );
		button.classList.toggle( 'is-active', shown );
		button.setAttribute( 'aria-pressed', String( shown ) );
		window.dispatchEvent( new Event( 'resize' ) );

	} );
	toggle( els.editorButton, 'is-editor-hidden' );
	toggle( els.outputButton, 'is-output-hidden' );

}

// ---- Splitters --------------------------------------------------------------

function dragSplit( handle, onMove ) {

	handle.addEventListener( 'pointerdown', ( event ) => {

		event.preventDefault();
		handle.setPointerCapture( event.pointerId );
		handle.classList.add( 'is-dragging' );
		document.body.classList.add( 'is-resizing' );
		const move = ( e ) => onMove( e );
		const up = () => {

			handle.classList.remove( 'is-dragging' );
			document.body.classList.remove( 'is-resizing' );
			handle.removeEventListener( 'pointermove', move );
			handle.removeEventListener( 'pointerup', up );
			handle.removeEventListener( 'pointercancel', up );

		};
		handle.addEventListener( 'pointermove', move );
		handle.addEventListener( 'pointerup', up );
		handle.addEventListener( 'pointercancel', up );

	} );

}

export function bindSplitters( els ) {

	dragSplit( els.vsplit, ( event ) => {

		const box = els.main.getBoundingClientRect();
		const width = Math.min( box.width - 240, Math.max( 280, event.clientX - box.left ) );
		els.root.style.setProperty( '--left-w', `${ width }px` );

	} );

	if ( els.hsplit ) dragSplit( els.hsplit, ( event ) => {

		const box = els.left.getBoundingClientRect();
		const height = Math.min( box.height - 160, Math.max( 64, box.bottom - event.clientY ) );
		els.root.style.setProperty( '--console-h', `${ height }px` );

	} );

}
